import React, { useState, useEffect } from 'react'
import {
  Dialog,
  useMediaQuery,
  useTheme,
  DialogContent,
  Grid,
  IconButton,
  Typography,
  Box,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

import useStyles from './styles'

import { ReactComponent as CloseIcon } from '../../assets/Icons_Close.svg'

export default function MediaGalleryDialog({ open, onClose, mediaItems }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const classes = useStyles()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0)
  const nextMedia = () => {
    let index = selectedMediaIndex + 1
    if (index >= mediaItems.length) {
      index = 0
    }
    setSelectedMedia(mediaItems[index])
    setSelectedMediaIndex(index)
  }
  const previousMedia = () => {
    let index = selectedMediaIndex - 1
    if (index < 0) {
      index = mediaItems.length - 1
    }
    setSelectedMedia(mediaItems[index])
    setSelectedMediaIndex(index)
  }
  useEffect(() => {
    if (mediaItems && mediaItems.length > 0) {
      setSelectedMedia(mediaItems[0])
      setSelectedMediaIndex(0)
    }
    // eslint-disable-next-line
  }, [mediaItems])

  // Render the appropriate media component based on media type
  const renderMedia = () => {
    if (!selectedMedia) return null

    const { url, mediaType } = selectedMedia

    // Handle old format image
    if (!mediaType) {
      return (
        <Box className={classes.imageWrapper}>
          <TransformWrapper
            key={`transform-${selectedMediaIndex}`}
            initialScale={1}
            initialPositionX={0}
            initialPositionY={0}
            minScale={0.5}
            maxScale={4}
            wheel={{ step: 0.1 }}
            doubleClick={{ mode: 'toggle' }}
            panning={{ disabled: false }}
            centerOnInit={true}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <Box className={classes.zoomControls}>
                  <IconButton
                    sx={{ color: theme.palette.primary.main }}
                    onClick={() => zoomIn()}
                    size="small"
                    title="Zoom avant"
                  >
                    <ZoomInIcon />
                  </IconButton>
                  <IconButton
                    sx={{ color: theme.palette.primary.main }}
                    onClick={() => zoomOut()}
                    size="small"
                    title="Zoom arrière"
                  >
                    <ZoomOutIcon />
                  </IconButton>
                  <IconButton
                    sx={{ color: theme.palette.primary.main }}
                    onClick={() => resetTransform()}
                    size="small"
                    title="Restaurer"
                  >
                    <RestartAltIcon />
                  </IconButton>
                </Box>
                <TransformComponent>
                  <img
                    src={selectedMedia}
                    alt="media content"
                    className={classes.image}
                    crossOrigin="anonymous"
                  />
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </Box>
      )
    }

    // Handle image
    if (mediaType === 'image') {
      return (
        <Box className={classes.imageWrapper}>
          <TransformWrapper
            key={`transform-${selectedMediaIndex}`}
            initialScale={1}
            initialPositionX={0}
            initialPositionY={0}
            minScale={0.5}
            maxScale={4}
            wheel={{ step: 0.1 }}
            doubleClick={{ mode: 'toggle' }}
            panning={{ disabled: false }}
            centerOnInit={true}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <Box className={classes.zoomControls}>
                  <IconButton
                    sx={{ color: theme.palette.primary.main }}
                    onClick={() => zoomIn()}
                    size="small"
                    title="Zoom avant"
                  >
                    <ZoomInIcon />
                  </IconButton>
                  <IconButton
                    sx={{ color: theme.palette.primary.main }}
                    onClick={() => zoomOut()}
                    size="small"
                    title="Zoom arrière"
                  >
                    <ZoomOutIcon />
                  </IconButton>
                  <IconButton
                    sx={{ color: theme.palette.primary.main }}
                    onClick={() => resetTransform()}
                    size="small"
                    title="Restaurer"
                  >
                    <RestartAltIcon />
                  </IconButton>
                </Box>
                <TransformComponent>
                  <img
                    src={url}
                    alt="media content"
                    className={classes.image}
                    crossOrigin="anonymous"
                  />
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </Box>
      )
    }

    // Handle video
    if (mediaType === 'video') {
      return (
        <Box className={classes.videoContainer}>
          <video
            src={url}
            controls
            className={classes.video}
            crossOrigin="anonymous"
            type="video/quicktime"
          >
            {t('media-gallery-dialog.video-not-supported')}
          </video>
        </Box>
      )
    }

    // Handle audio
    if (mediaType === 'audio') {
      return (
        <Box className={classes.audioContainer}>
          <audio
            src={url}
            controls
            className={classes.audio}
            crossOrigin="anonymous"
            type="audio/x-m4a"
          >
            {t('media-gallery-dialog.audio-not-supported')}
          </audio>
        </Box>
      )
    }

    // Fallback for unknown media types
    return (
      <Typography variant="body1" color="error" align="center">
        {t('media-gallery-dialog.media-type-not-supported', { mediaType })}
      </Typography>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          height: '80vh',
        },
      }}
    >
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <IconButton onClick={onClose} size="large">
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          style={{ height: 'calc(100% - 48px)', overflow: 'hidden' }}
        >
          {mediaItems && mediaItems.length > 1 && (
            <Grid item xs={1}>
              <IconButton onClick={previousMedia} size="large">
                <ArrowBackIosIcon />
              </IconButton>
            </Grid>
          )}
          <Grid
            item
            xs={10}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {renderMedia()}
          </Grid>
          {mediaItems && mediaItems.length > 1 && (
            <Grid item xs={1}>
              <IconButton onClick={nextMedia} size="large">
                <ArrowForwardIosIcon />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
